import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../../hooks/useFetch";

const RELATIONS = [
  { value: "mother", label: "Mother" },
  { value: "father", label: "Father" },
  { value: "grandparent", label: "Grandparent" },
  { value: "guardian", label: "Guardian" },
];

export default function ConsentGate({ userId, onConsentGiven }) {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();
  // const userId = authState?.user?._id;
  const userName = authState?.user?.name;
  const userCode = authState?.user?.think5whyscode;
  const [relation, setRelation] = useState("mother");
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState(4);
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });

  const canSubmit = childName.trim().length > 0 && ack && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitting(true);

    try {
      const payload = {
        userId,
        guardianRelation: relation,
        childName: childName.trim(),
        childAgeYears: Number(childAge),
        consentGiven: true,
        dataUseAcknowledged: true,
      };

      const response = await fetchData({
        url: "/tinythink/consent", 
        method: "post",
        data: payload,
        headers: { Authorization: authState.token },
      });

      if (response?.success) {
        setMessage({ text: "Consent saved", type: "success" });
        onConsentGiven();
      } else {
        setMessage({ text: "Could not save consent", type: "error" });
      }
    } catch (err) {
      setMessage({ text: "Failed to save consent", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#140346] text-sm text-[#e5e4e9] flex items-center justify-center p-4">
      <div className="bg-gray-600 text-white rounded-3xl shadow-sm max-w-sm w-full p-5 sm:p-6 space-y-4">
        <div className="text-center">
          <div className="text-4xl mb-2" aria-hidden="true">🧸</div>
          <h1 className="text-lg font-bold text-[#ad95e3]">Before we start playing</h1>
          <p className="text-xs sm:text-sm text-white mt-1">
            A quick one-time step — required to keep your child's play data safe and private.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-cyan-200 block mb-1">I am the child's</label>
            <div className="grid grid-cols-2 gap-2">
              {RELATIONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRelation(r.value)}
                  className={`text-xs sm:text-sm rounded-xl py-1 font-semibold transition-colors ${
                    relation === r.value ? "bg-[#7C3AED] text-white" : "bg-[#F3EEFF] text-[#2B2140]"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="childName" className="text-xs font-semibold text-gray-100 block mb-1">
              Child's first name
            </label>
            <input
              id="childName"
              type="text"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              placeholder="e.g. Aanya"
              className="w-full text-sm text-black rounded-xl border border-[#E5DEFF] px-3 py-1 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]"
            />
          </div>

          <div>
            <label htmlFor="childAge" className="text-xs font-semibold text-gray-100 block mb-1">
              Child's age
            </label>
            <select
              id="childAge"
              value={childAge}
              onChange={(e) => setChildAge(e.target.value)}
              className="w-full text-sm rounded-xl text-black border border-[#E5DEFF] px-1 py-1 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]"
            >
              <option value={3}>3 years</option>
              <option value={4}>4 years</option>
              <option value={5}>5 years</option>
            </select>
          </div>

          <label className="flex items-start gap-2 text-xs text-[#bfafe3] cursor-pointer">
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              className="mt-0.5 text-black accent-[#7C3AED]"
            />
            <span>
              I consent to PurpleDoor TinyThink collecting my child's play activity (scores, streaks) to run
              the game. This data is never used for ad-targeting.
            </span>
          </label>
        </div>

        {message.text && (
          <p className={`text-xs ${message.type === "error" ? "text-red-500" : "text-[#4ADE80]"}`}>
            {message.text}
          </p>
        )}

        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-fit px-2 py-1 bg-[#7C3AED] disabled:bg-[#D9CCFF] text-white font-bold rounded-2xl text-sm sm:text-base transition-colors"
        >
          {submitting ? "Saving…" : "Start Playing Together"}
        </button>
      </div>
    </div>
  );
}
